
import { formatPrice } from "@/app/infrastructures/misc/Utils";
import { ActivityController } from "@/app/ui/controllers/ActivityController";
import { SendActivityApiRequest } from "@/data/payload/api/ActivityApiRequest";
import { CustomerLeaderboardResponse } from "@/domain/entities/RevenueDashboard";
import moment from "moment";
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "tableLeaderboard",
  emits: ["loadMore", "sort"],
  props: {
    hasMore: {
      type: Boolean,
      default: true
    },
    data: {
      type: CustomerLeaderboardResponse,
      default: new CustomerLeaderboardResponse()
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    accountCurrency: {
      type: String,
      default: "IDR"
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const currency = computed(
      () => (props.accountCurrency as "IDR" | "MYR") || "IDR"
    );
    const scrollContainer = ref<HTMLDivElement | null>(null);
    const isLoadingMore = ref(false);

    const activeSortKey = ref<string>("");
    const activeOrderBy = ref<"asc" | "desc">("desc");

    const currentSortDirection = computed(() =>
      activeSortKey.value === "current_period" ? activeOrderBy.value : ""
    );

    function previousSortDirection(i: number) {
      const key = `previous_month_${i + 1}`;
      return activeSortKey.value === key ? activeOrderBy.value : "";
    }

    function sortCurrent() {
      if (activeSortKey.value === "current_period") {
        // Cycle through: desc -> asc -> default -> desc
        if (activeOrderBy.value === "desc") {
          activeOrderBy.value = "asc";
        } else if (activeOrderBy.value === "asc") {
          // Reset to default (no sorting)
          activeSortKey.value = "";
          activeOrderBy.value = "desc";
        }
      } else {
        activeSortKey.value = "current_period";
        activeOrderBy.value = "desc";
      }
      emit("sort", {
        sortBy:
          activeSortKey.value === "current_period" ? "current_period" : "",
        orderBy: activeOrderBy.value
      });
    }

    function sortPrevious(i: number) {
      const key = `previous_month_${i + 1}`;
      if (activeSortKey.value === key) {
        // Cycle through: desc -> asc -> default -> desc
        if (activeOrderBy.value === "desc") {
          activeOrderBy.value = "asc";
        } else if (activeOrderBy.value === "asc") {
          // Reset to default (no sorting)
          activeSortKey.value = "";
          activeOrderBy.value = "desc";
        }
      } else {
        activeSortKey.value = key;
        activeOrderBy.value = "desc";
      }
      emit("sort", {
        sortBy: activeSortKey.value === key ? key : "",
        orderBy: activeOrderBy.value
      });
    }
    const onScroll = () => {
      const container = scrollContainer.value;
      if (!container || isLoadingMore.value || !props.hasMore) return;

      const bottomReached =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 50;

      if (bottomReached) {
        isLoadingMore.value = true;
        emit("loadMore");
        setTimeout(() => {
          isLoadingMore.value = false;
        }, 500);
      }
    };
    const formatedDate = (dateString: string) => {
      return moment(dateString).format("DD MMMM YYYY, HH:mm");
    };

    const formatCurrency = (val: number) => {
      return formatPrice(val, "", currency.value);
    };

    const CHIP_MAP: Record<
      string,
      { i18nKey: string; bg: string; text: string; border?: string }
    > = {
      "Omzet Naik": {
        i18nKey: "dashboard.customer.omzetNaik",
        bg: "bg-green-lp-500",
        text: "text-gray-lp-300",
        border: "border border-green-lp-100"
      },
      "Omzet Turun": {
        i18nKey: "dashboard.customer.omzetTurun",
        bg: "bg-red-lp-50",
        text: "text-gray-lp-300",
        border: "border border-red-lp-750"
      },
      "Pelanggan Baru": {
        i18nKey: "dashboard.customer.pelangganBaru",
        bg: "bg-aqua-lp-200",
        text: "text-gray-lp-300",
        border: "border border-blue-lp-800"
      },
      "Pelanggan Churn": {
        i18nKey: "dashboard.customer.pelangganChurn",
        bg: "bg-red-lp-2700",
        text: "text-white"
      },
      "MISBOOKING > 5%": {
        i18nKey: "dashboard.customer.misbooking",
        bg: "bg-gray-lp-1000",
        text: "text-gray-lp-300",
        border: "border border-purple-lp-1300"
      },
      "RTS > 5%": {
        i18nKey: "dashboard.customer.rts",
        bg: "bg-purple-lp-100",
        text: "text-gray-lp-300",
        border: "border border-purple-lp-200"
      }
    };

    const DEFAULT_CHIP = {
      bg: "bg-gray-200",
      text: "text-gray-700",
      border: "border border-gray-300"
    };

    function mapTagsToChips(tags: string[]) {
      return tags.map((tag) => {
        const mapped = CHIP_MAP[tag];
        if (mapped)
          return {
            label: t(mapped.i18nKey),
            bg: mapped.bg,
            text: mapped.text,
            border: mapped.border
          };

        return {
          label: tag,
          ...DEFAULT_CHIP
        };
      });
    }

    const reloadPage = () => {
      window.location.reload();
    };

    const maskPhoneNumber = (phone: string) => {
      if (!phone) return "";
      if (phone.length <= 6) return phone;

      const start = phone.slice(0, 4);
      const end = phone.slice(-3);
      return `${start}****${end}`;
    };

    const openWhatsApp = async (phone: string, text = "") => {
      const cleaned = phone.replace(/[^0-9]/g, "");
      const url = `https://wa.me/${cleaned}?text=${encodeURIComponent(text)}`;
      await ActivityController.sendActivity(
        new SendActivityApiRequest({
          after: cleaned,
          before: cleaned,
          activityHeader: "pos-dashboard-click-wa"
        })
      );
      window.open(url, "_blank");
    };

    const currentDateFormatted = computed(() => {
      return moment().format("DD MMM YYYY");
    });

    return {
      reloadPage,
      formatedDate,
      maskPhoneNumber,
      openWhatsApp,
      mapTagsToChips,
      currentDateFormatted,
      formatCurrency,
      onScroll,
      scrollContainer,
      currentSortDirection,
      previousSortDirection,
      sortCurrent,
      sortPrevious
    };
  }
});
